// linear-spine-row — slot-layout primitive.
//
// Computes slot positions + orientations for a row of products standing
// on their spines along a horizontal axis, with comic-style fronts
// facing the customer. Used by longbox + shortbox today; any box-like
// or row-like archetype with spine-down stacking can adopt it via
// manifest.slots.layoutAlgorithm = "linear-spine-row".
//
// Manifest inputs (`manifest.slots`):
//   - productType         (string)  — the kapowxProductType to fill slots
//   - spacingMultiplier   (number)  — slot center-to-center = productThickness * this (default 1.2)
//   - marginMultiplier    (number)  — lane-end margin = productThickness * this (default 0.7)
//   - extractDistance     (number)  — hero-extract distance beyond lane.depth*0.5 (default 0.18m)
//   - extractHeightFactor (number)  — hero-extract height = productHeight * this (default 0.85)
//
// Spatial inputs:
//   - anchor.position.xyz   — lane bottom-centre in world space (the LONGBOX_ANCHOR convention)
//   - anchor.scale.x        — lane length (long axis, drives slot count)
//   - anchor.scale.y        — lane interior height (informational)
//   - anchor.scale.z        — lane depth (drives hero-extract distance)
//
// frontDir input (composer-supplied — Y-rotation of the fixture):
//   - The world-space unit vector pointing OUT the front of the lane.
//     Slot 1 sits at the front edge; subsequent slots step away from
//     front into the box.
//
// productCount input:
//   - Number of products GCG assigned to this anchor — drives the
//     adaptive slot count (clamped at maxSlots derived from lane length).
//
// Output:
//   - slots: Array<{ position: THREE.Vector3, quaternion: THREE.Quaternion }>
//     Per-slot world placement. slots[0] is the front-most; subsequent
//     slots step back into the lane.
//   - extras: {
//       laneCenter:      THREE.Vector3 — = anchor.position
//       laneSize:        THREE.Vector3 — (length, height, depth)
//       slotStepVector:  THREE.Vector3 — delta from slot N to slot N+1
//       slotQuaternion:  THREE.Quaternion — shared orientation for all slots
//       heroExtract:     THREE.Vector3 — extracted-product display position
//       maxSlots:        number        — max physically-fitting slots (clamped at productCount)
//       slotCount:       number        — actual slot count (min of maxSlots and productCount)
//     }

import * as THREE from "../../../../vendor/three.module.js";
import { boxedComicReferenceMeters } from "../../../authoring/contracts.js";
import { registerSlotLayout } from "./index.js";

function computeLinearSpineRow({ anchor, manifest, slotDef, variant, countOverride, frontDir, productCount }) {
	// v3 prefers `variant.params` for algorithm parameters; v2 callers
	// pass them on `slotDef` directly. Read variant.params first; fall
	// back to slot-level fields.
	const params = (variant?.params) || {};
	const legacy = slotDef || (Array.isArray(manifest?.slots) ? manifest.slots[0] : null) || {};
	const pick = (key, dflt) => {
		if (typeof params[key] === "number") return params[key];
		if (typeof legacy[key] === "number") return legacy[key];
		return dflt;
	};

	// Product reference dimensions. Today comic-only — when this primitive
	// generalises beyond comics, the reference comes from
	// `productCatalogue[productType].manifest.dimensions`.
	const COMIC_THICKNESS = boxedComicReferenceMeters.thickness;
	const COMIC_HEIGHT    = boxedComicReferenceMeters.height;

	const spacingMult = pick("spacingMultiplier",   1.2);
	const marginMult  = pick("marginMultiplier",    0.7);
	const extractDist = pick("extractDistance",     0.18);
	const extractHF   = pick("extractHeightFactor", 0.85);

	const slotSpacing = Math.max(COMIC_THICKNESS * spacingMult, 0.006);
	const slotMargin  = Math.max(COMIC_THICKNESS * marginMult,  0.016);

	const laneLength = Math.max(0.12, anchor.scale?.x ?? 0.12);
	const laneHeight = Math.max(0.08, anchor.scale?.y ?? 0.08);
	const laneDepth  = Math.max(0.08, anchor.scale?.z ?? 0.08);

	// Capacity (maxSlots) sourcing — composer's countOverride wins when
	// provided (v3 variant.count envelope resolved to a concrete number
	// after merchant-override clamping). Falls through to legacy v2
	// scalar slotDef.count, then to algorithm-derived from lane length.
	const usable = Math.max(0, laneLength - slotMargin * 2);
	let maxSlots;
	if (typeof countOverride === "number" && countOverride > 0) {
		maxSlots = Math.max(1, countOverride);
	} else if (typeof legacy.count === "number" && legacy.count > 0) {
		maxSlots = Math.max(1, legacy.count);
	} else {
		maxSlots = Math.max(1, Math.floor(usable / slotSpacing) + 1);
	}
	const slotCount = Math.min(maxSlots, Math.max(0, productCount));

	// Lane direction = frontDir. Slot 1 sits at the front edge of the box;
	// subsequent slots step away from front (into the box). Same as longbox
	// authoring convention 2026-05-20.
	const laneDir    = frontDir.clone();
	const extractDir = frontDir.clone();

	const slotOneOffset  = laneLength * 0.5 - slotMargin;
	const slotStepVector = laneDir.clone().multiplyScalar(-slotSpacing);
	const slotOne        = anchor.position.clone().addScaledVector(laneDir, slotOneOffset);

	// Product orientation: product local +Z (front-cover normal) must align
	// with frontDir so the customer sees fronts when flipping toward
	// themselves. Build the basis directly rather than going through a
	// normalising helper — normalisation loses front/back distinction.
	const upWorld = new THREE.Vector3(0, 1, 0);
	const xAxis = new THREE.Vector3().crossVectors(upWorld, frontDir).normalize();
	const slotQuaternion = new THREE.Quaternion().setFromRotationMatrix(
		new THREE.Matrix4().makeBasis(xAxis, upWorld, frontDir),
	);

	const slots = [];
	for (let i = 0; i < slotCount; i++) {
		slots.push({
			position:   slotOne.clone().add(slotStepVector.clone().multiplyScalar(i)),
			quaternion: slotQuaternion.clone(),
		});
	}

	const laneCenter = anchor.position.clone();
	const heroExtract = laneCenter.clone()
		.addScaledVector(extractDir, laneDepth * 0.5 + extractDist)
		.add(new THREE.Vector3(0, COMIC_HEIGHT * extractHF, 0));

	return {
		slots,
		extras: {
			laneCenter,
			laneSize: new THREE.Vector3(laneLength, laneHeight, laneDepth),
			slotStepVector,
			slotQuaternion,
			heroExtract,
			maxSlots,
			slotCount,
		},
	};
}

registerSlotLayout("linear-spine-row", computeLinearSpineRow);

// Exported for tests + composer-side imports that prefer the direct
// function reference over the registry lookup.
export { computeLinearSpineRow };
